import { queryKeys } from '@/lib/query-keys';
import { identityClient } from '@/lib/api/ts-rest';
import type {
  CreateIdentityBody,
  Identity,
  UpdateIdentityBody,
} from '@octave/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCurrentOrganization } from './useOrganization';

export const useUpdateIdentity = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Identity,
    Error,
    { id: string; updateInput: UpdateIdentityBody }
  >({
    mutationFn: async ({ id, updateInput }) => {
      const res = await identityClient.updateIdentity({
        params: { identityId: id },
        body: updateInput,
      });
      if (res.status !== 200) throw new Error('Failed to update identity');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.identities.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.identities.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail() });
    },
  });
};

export const useCreateIdentity = () => {
  const queryClient = useQueryClient();
  const { data: organization } = useCurrentOrganization();

  return useMutation<Identity, Error, CreateIdentityBody & { siteId: string }>({
    mutationFn: async ({ siteId, firstName, lastName }) => {
      if (!organization?.id) {
        throw new Error('Organization not loaded');
      }
      const res = await identityClient.createIdentity({
        params: { organizationId: organization.id, siteId },
        body: { firstName, lastName },
      });
      if (res.status !== 201) throw new Error('Failed to create identity');
      return res.body;
    },
    onSuccess: () => {
      toast.success('Identité créée avec succès');
      queryClient.invalidateQueries({ queryKey: queryKeys.identities.root() });
    },
    onError: () => {
      toast.error('Oups, une erreur est survenue !');
    },
  });
};
